import React, { useEffect, useState } from "react";
import { Button, Divider, Form, Input, message, Modal, Segmented, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { addDoc, collection, doc, getDoc, getDocs, writeBatch } from "firebase/firestore";
import dayjs from "dayjs";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import QuantityControl from "components/shared-components/QuantityControl";
import useIsMobile from "../../../../../hooks/useIsMobile";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { updateCountedStock } from "../../../../../functions/api/stock";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { firestore } from "../../../../../configs/FirebaseConfig";
import {
  FIRESTORE_CRATE_STOCK_LOGS_TABLE,
  FIRESTORE_CRATE_STOCKS_TABLE,
  FIRESTORE_PRODUCTS_STOCK_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import {
  ADJUSTMENT_DIRECTION_ADD,
  ADJUSTMENT_DIRECTION_REMOVE,
  CRATE_ADDITION_REASONS,
  CRATE_ADJUSTMENT_REASONS,
  LOG_ACTION_QUANTITY_ADJUSTED,
  REASON_OTHER,
  STATUS_AVAILABLE,
  STATUS_IN_USE,
} from "../../../../../constants/CratesConstants";

const { Text } = Typography;

const AdjustCrateQuantityModal = ({ open, onCancel, crate, crateItem, updateCrateStock, addCrateEvent }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { uid, email, profile } = useAuth();
  const isMobile = useIsMobile();
  const [submitting, setSubmitting] = useState(false);
  const [direction, setDirection] = useState(ADJUSTMENT_DIRECTION_REMOVE);
  const [changeAmount, setChangeAmount] = useState(0);

  const watchedReason = Form.useWatch("reason", form);

  const isAdding = direction === ADJUSTMENT_DIRECTION_ADD;
  const reasonOptions = isAdding ? CRATE_ADDITION_REASONS : CRATE_ADJUSTMENT_REASONS;

  const currentQuantity = Number(crateItem?.quantity ?? 0);
  const newQuantity = isAdding ? currentQuantity + changeAmount : currentQuantity - changeAmount;

  useEffect(() => {
    if (open) {
      setDirection(ADJUSTMENT_DIRECTION_REMOVE);
      setChangeAmount(0);
      form.setFieldsValue({ reason: undefined, reasonNote: "" });
    } else {
      form.resetFields();
      setDirection(ADJUSTMENT_DIRECTION_REMOVE);
      setChangeAmount(0);
    }
  }, [open, form]);

  const handleDirectionChange = (value) => {
    setDirection(value);
    setChangeAmount(0);
    form.setFieldsValue({ reason: undefined, reasonNote: "" });
  };

  const handleIncrease = () => setChangeAmount((prev) => (isAdding ? prev + 1 : Math.min(prev + 1, currentQuantity)));
  const handleDecrease = () => setChangeAmount((prev) => Math.max(prev - 1, 0));
  const handleManualInput = (_, value) => {
    const clamped = isAdding
      ? Math.max(0, Number(value) || 0)
      : Math.min(Math.max(0, Number(value) || 0), currentQuantity);
    setChangeAmount(clamped);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const oldQuantity = currentQuantity;
      const delta = isAdding ? changeAmount : -changeAmount;

      const existingItems = Array.isArray(crate?.items) ? crate.items : [];
      const updatedItems = existingItems
        .map((item) => {
          if (item.productId === crateItem.productId) {
            return { ...item, quantity: newQuantity };
          }
          return item;
        })
        .filter((item) => Number(item.quantity) > 0);

      const nextItemsCount = updatedItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
      const nextStatus = nextItemsCount === 0 ? STATUS_AVAILABLE : STATUS_IN_USE;

      const userName =
        profile?.displayName || profile?.name || profile?.fullName || profile?.email || email || uid || null;

      await updateCrateStock(
        crate.id,
        {
          items: updatedItems,
          itemsCount: nextItemsCount,
          status: nextStatus,
        },
        uid || email || null,
      );

      await addCrateEvent(crate.id, {
        type: LOG_ACTION_QUANTITY_ADJUSTED,
        crateId: crate.crateId,
        productId: crateItem.productId,
        productName: crateItem.productName || null,
        quantityBefore: oldQuantity,
        quantity: newQuantity,
        delta,
        reason: values.reason,
        reasonNote: values.reason === REASON_OTHER ? values.reasonNote || null : null,
        company: crate.company || null,
        createdByUid: uid || null,
        createdBy: email || null,
      });

      if (nextItemsCount === 0) {
        const crateDocId = crate.id;
        const eventsColRef = collection(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_CRATE_STOCKS_TABLE,
          crateDocId,
          "events",
        );
        const eventsSnap = await getDocs(eventsColRef);
        const events = [];
        eventsSnap.forEach((d) => {
          events.push({ id: d.id, ...d.data() });
        });

        if (events.length) {
          const logColRef = collection(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_CRATE_STOCK_LOGS_TABLE,
          );
          await addDoc(logColRef, {
            crateId: crate.crateId || crateDocId,
            crateDocId,
            company: crate.company || null,
            productId: crateItem.productId || null,
            productBarcode: crateItem.barcode || null,
            events,
            createdAt: dayjs().unix(),
          });
        }

        const batch = writeBatch(firestore);
        eventsSnap.forEach((d) => {
          batch.delete(d.ref);
        });
        await batch.commit();
      }

      // Keep product counted stock in sync with the crate adjustment direction
      if (changeAmount > 0 && crateItem.packageDate) {
        try {
          const token = await getFirebaseIDToken();
          const dateId = dayjs().format("DDMMYYYY");
          const stockDocRef = doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_PRODUCTS_STOCK_TABLE,
            dateId,
          );
          const stockDoc = await getDoc(stockDocRef);
          const currentCountedStock = Number(stockDoc.data()?.products?.[crateItem.productId]?.countedStock || 0);
          const nextCountedStock = isAdding
            ? currentCountedStock + changeAmount
            : Math.max(0, currentCountedStock - changeAmount);
          // trackAddedRemoved = true only when adding stock (mirrors incoming goods registration)
          await updateCountedStock(
            crateItem.productId,
            dateId,
            nextCountedStock,
            COMPANY_COUNTRY_CODE,
            token,
            isAdding,
          );
        } catch (stockError) {
          console.error("Error updating product stock:", stockError);
          message.warning(t("crate_stock.stock_update_failed_server"));
        }
      }

      if (nextItemsCount === 0) {
        message.success(t("crate_stock.crate_marked_available_reuse"));
      } else {
        message.success(t("crate_stock.quantity_adjusted_success"));
      }

      onCancel?.();
    } catch (e) {
      if (e?.errorFields) return;
      console.error("Error adjusting crate quantity:", e);
      message.error(t("crate_stock.quantity_adjusted_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t("crate_stock.adjust_quantity_title")}
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      footer={
        <Space>
          <Button onClick={onCancel} disabled={submitting}>
            {t("cancel")}
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={submitting}>
            {t("crate_stock.submit")}
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" form={form}>
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary">
            {t("crate_stock.crate_label")} {crate?.crateId || "-"}&nbsp;&nbsp;|&nbsp;&nbsp;
            {crateItem?.productName || crateItem?.productId || "-"}&nbsp;&nbsp;|&nbsp;&nbsp;
            {t("crate_stock.package_date")}: {crateItem?.packageDate || "-"}
          </Text>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <Segmented
            value={direction}
            onChange={handleDirectionChange}
            options={[
              { label: t("crate_stock.direction_remove"), value: ADJUSTMENT_DIRECTION_REMOVE },
              { label: t("crate_stock.direction_add"), value: ADJUSTMENT_DIRECTION_ADD },
            ]}
          />
        </div>

        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>
            {isAdding ? t("crate_stock.add_amount_label") : t("crate_stock.remove_amount_label")}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <QuantityControl
              productId="adjust-change"
              currentQuantity={changeAmount}
              onIncrease={handleIncrease}
              onDecrease={handleDecrease}
              onManualInput={handleManualInput}
              isMobile={isMobile}
              allowNegative={false}
            />
          </div>
          <div style={{ fontSize: 14, lineHeight: 2.2 }}>
            <div>
              <span style={{ color: "#666" }}>{t("current")}: </span>
              <strong>{currentQuantity}</strong>
            </div>
            <div>
              <span style={{ color: "#666" }}>{t("change")}: </span>
              <strong style={{ color: changeAmount === 0 ? "#888" : isAdding ? "#389e0d" : "#f5222d" }}>
                {changeAmount > 0 ? `${isAdding ? "+" : "-"}${changeAmount}` : "0"}
              </strong>
            </div>
            <div>
              <span style={{ color: "#666" }}>{t("new_value")}: </span>
              <strong style={{ fontSize: 20, color: newQuantity === 0 ? "#faad14" : "#1a3353" }}>{newQuantity}</strong>
            </div>
            {!isAdding && newQuantity === 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="warning" style={{ fontSize: 12 }}>
                  ⚠ {t("crate_stock.crate_becomes_available_hint")}
                </Text>
              </div>
            )}
          </div>
        </div>

        <Divider />

        <Form.Item
          name="reason"
          label={t("crate_stock.reason_label")}
          rules={[{ required: true, message: t("crate_stock.select_reason") }]}
        >
          <Select placeholder={t("crate_stock.select_reason")}>
            {reasonOptions.map((reason) => (
              <Select.Option key={reason.value} value={reason.value}>
                {t(reason.translationKey)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {watchedReason === REASON_OTHER && (
          <Form.Item
            name="reasonNote"
            label={t("crate_stock.reason_note_label")}
            rules={[{ required: true, message: t("crate_stock.enter_reason_note") }]}
          >
            <Input placeholder={t("crate_stock.reason_note_placeholder")} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AdjustCrateQuantityModal;
